// NodeDateInput.jsx

import React, { useEffect, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { isCompleteRawDate } from 'node/form/fields/utilities/dateFieldUtils';
import { normalizeDateValue, timestampFromDate } from 'utilities/DateTime';
import { clearDates } from 'utilities/Events';

// Components
import DatePicker from 'components/datepicker/DatePicker';
import IconButton from 'components/iconbutton/IconButton';

/**
 * NodeDateInput
 *
 * Shared date input for schema-driven date fields.
 *
 * @param {object} value - Current field value.
 * @param {Function} onChange - Callback when new dates are saved.
 * @param {string} className - Optional class name for the DatePicker.
 * @param {string} startIconKey - Icon name for the date picker.
 * @param {string} startIconColor - Icon color override.
 * @param {number} iconSize - Icon size.
 * @param {boolean} hybrid - Whether to use hybrid input mode.
 * @param {Intl.DateTimeFormatOptions|null} friendlyFormatOptions - Optional idle-label format for hybrid mode.
 * @param {string|number|null} friendlyFontWeight - Optional idle-label font-weight for hybrid mode.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateInput = ({
  value,
  onChange,
  className,
  startIconKey = 'calendar_month',
  startIconColor,
  iconSize = 22,
  hybrid = true,
  friendlyFormatOptions = null,
  friendlyFontWeight = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [currentDate, setCurrentDate] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);

  // ----- REFS ----- //

  const rawValueRef = useRef('');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setCurrentDate(normalizeDateValue(value?.startDate));
  }, [value]);

  // ----- UTILITIES ----- //

  const buildSingleDateValue = (date) => ({
    startDate: timestampFromDate(date),
    startPrecision: 'date',
    endDate: null,
    endPrecision: null,
    isRange: false,
  });

  // ----- HANDLERS ----- //

  /**
   * Handles date changes from the DatePicker, updating local state and 
   * calling onChange with the new value.
   * 
   * @param {Date|null} nextDate - The new date value from the DatePicker.
   */

  const handleRawChange = (rawValue) => {
    rawValueRef.current = rawValue || '';
    if (!isCompleteRawDate(rawValue)) {
      setIsInvalid(false);
    }
  };

  /**
   * Handles date changes from the DatePicker, updating local state and 
   * calling onChange with the new value.
   * 
   * @param {Date|null} nextDate - The new date value from the DatePicker.
   */
  const handleDateChange = (nextDate) => {
    setCurrentDate(nextDate);

    if (typeof onChange !== 'function') {
      return;
    }

    if (nextDate) {
      setIsInvalid(false);
      onChange(buildSingleDateValue(nextDate));
      return;
    }

    const rawValue = rawValueRef.current || '';
    if (!rawValue) {
      setIsInvalid(false);
      onChange(clearDates());
      return;
    }

    if (isCompleteRawDate(rawValue)) {
      setIsInvalid(true);
    }
  };

  /**
 * Handles clear click.
 */
  const handleClearClick = () => {
    rawValueRef.current = '';
    setCurrentDate(null);
    setIsInvalid(false);

    if (typeof onChange === 'function') {
      onChange(clearDates());
    }
  };

  // ----- RENDER ----- //

  return (

    <DatePicker
      className={className}
      value={currentDate}
      inputClassName={isInvalid ? 'date-picker-invalid' : ''}
      startIconKey={startIconKey}
      startIconColor={startIconColor ?? theme.foregroundMuted}
      iconSize={iconSize}
      hybrid={hybrid}
      friendlyFormatOptions={friendlyFormatOptions}
      friendlyFontWeight={friendlyFontWeight}
      endAction={currentDate ? (
        <div
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleClearClick();
          }}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <IconButton
            iconKey="close"
            iconSize={18}
            title="Clear date"
          />
        </div>
      ) : null}
      onChange={handleDateChange}
      onRawChange={handleRawChange}
    />

  );
};

// Dev Tools
NodeDateInput.displayName = 'NodeDateInput';

export default NodeDateInput;
